import { useCallback, useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import type { HomeLiveState } from '@shared/homeLive';

function wantsSound() {
  try { return window.sessionStorage.getItem('home-live-sound') !== 'off'; }
  catch { return true; }
}
function rememberSound(on: boolean) {
  try { window.sessionStorage.setItem('home-live-sound', on ? 'on' : 'off'); }
  catch { /* Playback also works when storage is disabled. */ }
}
export function useHomeLiveAudio(round: HomeLiveState | null, offset: number, suspended: boolean) {
  const audio = useRef<HTMLAudioElement>(null);
  const generation = useRef(0), opted = useRef(wantsSound()), volumeRef = useRef(.2);
  const latest = useRef({round, offset, suspended}); latest.current = {round, offset, suspended};
  const [volume, setVolume] = useState(.2), [enabled, setEnabled] = useState(false);
  const [playing, setPlaying] = useState(false), [error, setError] = useState('');
  const allowed = useCallback((a: HTMLAudioElement) => {
    const {round:r, offset:o, suspended:s} = latest.current;
    return audio.current === a && opted.current && !s && !document.hidden && !!r?.previewUrl
      && r.startsAt !== null && r.endsAt !== null && Date.now() + o < r.endsAt;
  }, []);
  const start = useCallback(() => {
    const a = audio.current, r = latest.current.round, token = generation.current;
    if (!a || !r || !allowed(a)) return;
    const seek = () => {
      if (generation.current !== token || !allowed(a)) return;
      try { a.currentTime = Math.max(0, (Date.now() + latest.current.offset - r.startsAt!) / 1000); }
      catch { /* Metadata pending. */ }
    };
    seek(); a.onloadedmetadata = seek;
    const rejected = (reason: unknown) => {
      if (generation.current !== token || !allowed(a)) return;
      setPlaying(false); setEnabled(false);
      if ((reason as {name?: string})?.name === 'NotAllowedError') {
        opted.current = false;
        setError('Clique sur « Activer le son » pour écouter le direct.');
      } else setError('Lecture impossible. Réessaie le son.');
    };
    try {
      a.play()?.then(() => {
        if (generation.current !== token || !allowed(a)) {
          if (audio.current !== a || !allowed(a)) a.pause();
          return;
        }
        setEnabled(true); setError('');
      }).catch(rejected);
    } catch (reason) { rejected(reason); }
  }, [allowed]);
  useEffect(() => {
    const a = audio.current; if (!a) return;
    generation.current++; a.pause(); a.onloadedmetadata = null;
    setPlaying(false); if (opted.current) setError(''); a.volume = volumeRef.current;
    if (round?.previewUrl) {
      a.src = round.previewUrl;
      if (opted.current && !suspended && !document.hidden) start();
    } else a.removeAttribute('src');
    return () => { generation.current++; a.onloadedmetadata = null; a.pause(); };
  }, [round?.roundId, round?.previewUrl, suspended, start]);
  useEffect(() => {
    const visibility = () => {
      generation.current++;
      if (document.hidden) { audio.current?.pause(); setPlaying(false); }
      else start();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, [start]);
  const toggle = () => {
    generation.current++; setError('');
    if (enabled) {
      opted.current = false; rememberSound(false); setEnabled(false);
      audio.current?.pause(); setPlaying(false);
    } else { opted.current = true; rememberSound(true); start(); }
  };
  const retry = () => { setError(''); generation.current++; start(); };
  const changeVolume = (value: number) => {
    const next = Math.min(1, Math.max(0, value));
    volumeRef.current = next; setVolume(next); if (audio.current) audio.current.volume = next;
  };
  const valid = (e: SyntheticEvent<HTMLAudioElement>) => e.currentTarget === audio.current && allowed(e.currentTarget);
  return {
    audio, enabled, playing, error, toggle, retry, volume, changeVolume,
    onPlaying: (e: SyntheticEvent<HTMLAudioElement>) => { if (valid(e) && !e.currentTarget.paused) { setPlaying(true); setEnabled(true); } },
    onWaiting: (e: SyntheticEvent<HTMLAudioElement>) => { if (e.currentTarget === audio.current) setPlaying(false); },
    onPause: (e: SyntheticEvent<HTMLAudioElement>) => { if (e.currentTarget === audio.current && e.currentTarget.paused) setPlaying(false); },
    onEnded: (e: SyntheticEvent<HTMLAudioElement>) => { if (valid(e) && e.currentTarget.ended) { setPlaying(false); setError('Extrait terminé.'); } },
    onError: (e: SyntheticEvent<HTMLAudioElement>) => { if (valid(e) && e.currentTarget.error) { setPlaying(false); setError('Extrait audio indisponible.'); } },
  };
}
